import React, { useEffect, useState } from 'react';
import { Show, useShowContext, useNotify, usePermissions, useTranslate } from 'react-admin';

// Components
import EmptyList from '../../components/misc/EmptyList';
import EventHeader from '../../components/events/EventHeader';
import EventDetails from '../../components/events/EventDetails';
import EventSummary from '../../components/events/EventSummary';
import EventReports from '../../components/events/EventReports';
import EventActions from '../../components/events/EventActions';

// Services
import { fetchEventSalesData, fetchCancelJob } from '../../services/eventService';
import { getEventDbId } from '../../utils/eventIdentity';

// Utils
import { computeEventStatus, EVENT_STATUS } from '../../utils/eventStatus';
import CancelEventDialog, { ACTIVE_JOB_STATUSES } from '../../components/events/CancelEventDialog';
import PostponeEventDialog from '../../components/events/PostponeEventDialog';

const EventStockReport = () => {
  const translate = useTranslate();
  const { record } = useShowContext();
  const event = record;
  const notify = useNotify();
  const { permissions } = usePermissions();
  const eventDbId = getEventDbId(event);

  const [statsLoading, setStatsLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [postponeOpen, setPostponeOpen] = useState(false);
  const [eventStatsData, setEventStatsData] = useState({
    totalRevenue: 0,
    totalSales: 0,
    totalTickets: 0,
    totalSeats: 0,
    totalRemaining: 0,
    totalCouponDiscount: 0,
    totalCommssionAmount: 0,
    totalAmountOfCashSales: 0,
  });

  const isAdmin = permissions.crole && permissions.crole === 'admin';

  useEffect(() => {
    const loadEventData = async () => {
      if (!eventDbId) return;
      setStatsLoading(true);
      try {
        const stats = await fetchEventSalesData(eventDbId);
        setEventStatsData(stats);
      } catch (error) {
        console.error('Error loading event data:', error);
        notify(translate('show.error_loading_event_data', { _: 'Error loading event data' }), {
          type: 'error',
        });
      } finally {
        setStatsLoading(false);
      }
    };

    loadEventData();
  }, [eventDbId, notify, translate]);

  // Devam eden bir iptal/iade job'u varsa (sekme/sayfa değişip dönülmüşse)
  // ilerleme ekranını otomatik geri aç.
  useEffect(() => {
    if (!eventDbId || !isAdmin) return;
    let ignore = false;
    fetchCancelJob(eventDbId)
      .then((res) => {
        const job = res?.data;
        if (!ignore && job && ACTIVE_JOB_STATUSES.includes(job.status)) {
          setCancelOpen(true);
        }
      })
      .catch(() => {
        /* job yoksa / yetki yoksa sessiz geç */
      });
    return () => {
      ignore = true;
    };
  }, [eventDbId, isAdmin]);

  const handleCancelEvent = () => {
    setCancelOpen(true);
  };

  if (!event) return <EmptyList mode={'loading'} />;

  const eventStatus = computeEventStatus(event);
  const hasEventPassed = eventStatus.key === EVENT_STATUS.ENDED;

  return (
    <div style={{ paddingLeft: 30, paddingRight: 30, fontSize: 16 }}>
      <EventHeader event={event} />

      <div
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          borderBottom: '2px solid #EEE',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'row', borderBottom: '2px solid #EEE' }}>
          <EventDetails event={event} hasEventPassed={hasEventPassed} />

          <div>
            <h2>{translate('show.sales_summary', { _: 'Sales Summary' })}</h2>
            {statsLoading ? (
              <span style={{ color: '#6b7280' }}>
                {translate('show.loading_sales_summary', { _: 'Sales summary yükleniyor…' })}
              </span>
            ) : (
              <EventSummary event={event} eventStatsData={eventStatsData} />
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            borderBottom: '2px solid #EEE',
            paddingBottom: 10,
          }}
        >
          <EventReports event={event} record={record} />
        </div>

        <EventActions
          event={event}
          record={record}
          hasEventPassed={hasEventPassed}
          isAdmin={isAdmin}
          isApproved={event.isApproved === true}
          onCancelEvent={handleCancelEvent}
          onPostponeEvent={() => setPostponeOpen(true)}
          permissions={permissions}
        />
      </div>

      <PostponeEventDialog
        open={postponeOpen}
        event={event}
        eventId={eventDbId}
        notify={notify}
        onClose={() => setPostponeOpen(false)}
        onDone={() => {
          window.location.reload();
        }}
      />

      <CancelEventDialog
        open={cancelOpen}
        eventId={eventDbId}
        eventName={event?.name}
        notify={notify}
        onClose={() => setCancelOpen(false)}
        onDone={() => {
          window.location.href = '/#/events-v2';
        }}
      />
    </div>
  );
};

const EventV2Show = (props) => {
  return (
    <Show
      title={<EventTitle />}
      // Tarayıcı sekmesine geri dönünce kaydı yeniden çekme — refetch,
      // iptal edilmiş etkinlikte sayfayı/modalı sıfırlayıp kaybettiriyor.
      queryOptions={{ refetchOnWindowFocus: false, refetchOnReconnect: false }}
      {...props}
    >
      <EventStockReport {...props} />
    </Show>
  );
};

const EventTitle = () => {
  const record = useShowContext()?.record;
  if (!record) return null;
  return <span>{record.slug || record.name || record.id}</span>;
};

export default EventV2Show;
